import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ContentTemplate,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TableState,
  TitleHeader,
  MUIDataTableColumn
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import {
  MUIDataTableCustomFilterListOptions,
  useDataTable,
} from 'web-library/stories/organism/DataTable/DataTable';
import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import { AUTHORITY_NAMES, FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import DownloadFromS3Action from '../../../components/buttons/downloadFromS3Action';
import { customFilterChipRender } from '../../../hooks/table';
import ExamLevelMultiSelect from '../../../components/select/ExamLevelMultiSelect';
import SchoolMultiSelect from '../../../components/select/SchoolMultiSelect';
import PaymentModeMultiSelect from '../../../components/select/PaymentModeMultiSelect';
import * as QUERIES from '../../../api/candidate/resit/@opadmin/queries-services/queries';
import { useTriggerMutate } from '../../../hooks/query';
import RouteKey from '../../../routes/key';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';
import { FileUploadState } from '../../../models/File';
import { RetrieveS3URLResponse } from '../../../api/document/files/api-services/model';
import { CodeValueResponse, KeyValueResponse } from '../../../api';
import { useGetCheckAuthorityExist } from '../../../api-refactored/user/useGetCheckAuthorityExist';

const useStyles = makeStyles()((theme) =>
({
  tableSearchContainer: {
    paddingBottom: theme.spacing(2),
  },
  viewDetailsButton: {
    whiteSpace: 'nowrap',
    display: 'flex',
    alignSelf: 'flex-start',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
    color: theme.color.link.linkDefault01,
  },
}));

/** common column options
 * */
const commonTableOption = {
  searchable: false,
  filter: false,
};

/** MUI Data Table Columns
 * */
const columns: MUIDataTableColumnDef[] = [
  {
    name: 'id',
    label: 'NRIC/FIN/Passport No.',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'indexNumber',
    label: 'Index Number',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'statutoryName',
    label: 'Statutory Name',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'applicationDate',
    label: 'Application Date',
    options: {
      ...commonTableOption,
      sort: true,
      filter: false,
    },
  },
  {
    name: 'examLevel',
    label: 'Exam Level',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'examFee',
    label: 'Exam Fee (with GST)',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'paymentMode',
    label: 'Payment Mode',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'paymentDateTime',
    label: 'Payment Date/Time',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'school',
    label: 'School',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
  {
    name: 'action',
    label: 'Action',
    options: {
      ...commonTableOption,
      sort: false,
      filter: false,
    },
  },
];

/** init data table customFilterListOptions
 * */
const customFilterOptionListRender = (
  dispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  tableName: string,
  selectedFilters: any
): MUIDataTableCustomFilterListOptions => ({
  render: (items: any) => {
    if (selectedFilters[tableName] && selectedFilters[tableName].length > 1 && items.length > 1) {
      return [`${selectedFilters[tableName][0]}/${selectedFilters[tableName][1]}`];
    }
    if (
      selectedFilters[tableName] &&
      selectedFilters[tableName].length === 1 &&
      items.length === 1
    ) {
      return [`${selectedFilters[tableName][0]}`];
    }
    return [];
  },
  update: (filterList: string[][],
    filterPos: number,
    index: number) => {
    /** update table filter state
     * */
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: { ...state.filters, action: [], [tableName]: [] },
    });

    /** update table inner filter
     * */
    return customFilterChipRender(filterList, filterPos, index);
  },
});

/** init custom filter for Exam Level
 * */
const addExamLevelCustomFilter = (
  dispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  updateSelectedValues: (selectedValues: any[], columnName: string) => void,
  selectedExamLevel: any
): MUIDataTableColumnDef => ({
  name: 'examLevel',
  label: 'Exam Level',
  options: {
    sort: false,
    filterType: 'custom',
    filter: true,
    customFilterListOptions: customFilterOptionListRender(
      dispatch,
      state,
      'examLevel',
      selectedExamLevel
    ),
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <ExamLevelMultiSelect
          // eslint-disable-next-line react/destructuring-assignment
          value={filterList[index]}
          accessFunction={FUNCTION_NAMES.RESIT_REPORT}
          label="Exam Level"
          onChange={(selectedValues) => {
            updateSelectedValues(selectedValues, 'examLevel');
            // eslint-disable-next-line no-param-reassign
            filterList[index] = selectedValues.map((s) => s.codeValue);
            onChange(filterList[index], index, column);
          }}
        />
      ),
    },
  },
});

/** init custom filter for School Code
 * */
const addPaymentModeCustomFilter = (
  dispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  updateSelectedValues: (selectedValues: any[], columnName: string) => void,
  selectedPaymentMode: any
): MUIDataTableColumnDef => ({
  name: 'paymentMode',
  label: 'Payment Mode',
  options: {
    filter: true,
    sort: false,
    filterType: 'custom',
    customFilterListOptions: customFilterOptionListRender(
      dispatch,
      state,
      'paymentMode',
      selectedPaymentMode
    ),
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <PaymentModeMultiSelect
          // eslint-disable-next-line react/destructuring-assignment
          value={filterList[index]}
          label="Payment Mode"
          onChange={(selectedValues) => {
            updateSelectedValues(selectedValues, 'paymentMode');
            // eslint-disable-next-line no-param-reassign
            filterList[index] = selectedValues.map((s) => s.code) as string[];
            onChange(filterList[index], index, column);
          }}
        />
      ),
    },
  },
});

/** init custom filter for School Description
 * */
const addSchoolDescriptionCustomFilter = (
  dispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  updateSelectedValues: (selectedValues: any[], columnName: string) => void,
  selectedSchool: any
): MUIDataTableColumnDef => ({
  name: 'school',
  label: 'School',
  options: {
    filter: true,
    sort: false,
    filterType: 'custom',
    customFilterListOptions: customFilterOptionListRender(
      dispatch,
      state,
      'school',
      selectedSchool
    ),
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <SchoolMultiSelect
          // eslint-disable-next-line react/destructuring-assignment
          value={filterList[index]}
          label="School"
          onChange={(selectedValues) => {
            updateSelectedValues(selectedValues, 'school');
            // eslint-disable-next-line no-param-reassign
            filterList[index] = selectedValues.map((s) => s.codeId);
            onChange(filterList[index], index, column);
          }}
        />
      ),
    },
  },
});

/** init view detail action to view the full transaction details
 * */
const createViewDetailsActionColumnDef = (
  className: string,
  onViewDetails: any,
  t: TFunction<'translation'>
): MUIDataTableColumnDef => {
  const addViewDetailsAction: MUIDataTableColumnDef = {
    name: 'action',
    label: 'Action',
    options: {
      sort: false,
      filter: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Button
          className={className}
          variant="text"
          onClick={() => {
            onViewDetails(dataIndex);
          }}
          size="small"
        >
          {t('resit->>OpAdminResit.View Details')}
        </Button>
      ),
    },
  };
  return addViewDetailsAction;
};

/** Route Key
 * */
const { oopAdminResitRegisteredExam, oopAdminIndividualResitRegisteredExam } = RouteKey.admin.child;

const OopAdminResitRegisterDetail = () => {
  const { classes } = useStyles();
  const history = useHistory();

  const [fileUUID, setFileUUID] = useState<string>('');
  const [dialogState, setDialogState] = useState<boolean>(false);
  const [downloadStatus, setDownloadStatus] = useState<FileUploadState>(FileUploadState.IDLE);
  const [examSelectedValues, setExamSelectedValues] = useState<CodeValueResponse[]>([]);
  const [schoolSelectedValues, setSchoolSelectedValues] = useState<CodeValueResponse[]>([]);
  const [paymentSelectedValues, setPaymentSelectedValues] = useState<KeyValueResponse[]>([]);
  const [filterredList, setFilterredList] = useState({});

  const { dispatch, tOptions, selectionToolbar, state } = useDataTable();

  const { t } = useTranslation();

  useGetCheckAuthorityExist({
    authorityName: AUTHORITY_NAMES.CP_ADMIN_RESIT_REPORT_VIEW,
  });

  /** Query to post download data
   * */
  const { mutate: onDownload } = QUERIES.useOopAdminCandidateResitRegisterDetailDownload(
    (res) => {
      if (res && res.data) {
        setFileUUID(res.data.uuid);
      }
    },
    (_errors) => {
      // Do Nothing
    }
  );

  /** handle download action
   * */
  const onDownloadTemplate = (response: RetrieveS3URLResponse) => {
    setDialogState(true);
    if (response.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogState(false);
    }
    setDownloadStatus(response.fileStatus as FileUploadState);
  };

  /** handle dialog close
   * */
  const onDialogClose = () => {
    setDialogState(false);
    setFileUUID('');
  };

  /** Query to fetch registered information
   * */
  const { mutate, status: listingStatus } = QUERIES.useOopAdminCandidateResitRegisterDetail(
    (response) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: response.data.content,
          count: response.data.totalElements,
        },
      } as TableActionTypes);
    },
    (_error) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: [],
          count: 0,
        },
      } as TableActionTypes);
    }
  );

  /** to save the filter selected values
   * */
  const updateSelectedState = (selectedValues: any[], columnName: string) => {
    if (columnName === 'examLevel') {
      setExamSelectedValues(selectedValues);
    } else if (columnName === 'paymentMode') {
      setPaymentSelectedValues(selectedValues);
    } else {
      setSchoolSelectedValues(selectedValues);
    }
  };

  /** init Data Table Columns
   * */
  const initColumns = useMemo<MUIDataTableColumnDef[]>(() => {
    /** handle on view detail action
     * */
    const onViewDetails = (dataIndexNo: number) => {
      if (state.data.length > 0) {
        history.push(
          `/admin/${oopAdminResitRegisteredExam.route}/${oopAdminIndividualResitRegisteredExam.route}`,
          { indexNo: state.data[dataIndexNo].indexNumber }
        );
      }
    };
    columns[4] = addExamLevelCustomFilter(dispatch, state, updateSelectedState, filterredList);
    columns[6] = addPaymentModeCustomFilter(dispatch, state, updateSelectedState, filterredList);
    columns[8] = addSchoolDescriptionCustomFilter(
      dispatch,
      state,
      updateSelectedState,
      filterredList
    );
    columns[9] = createViewDetailsActionColumnDef(classes.viewDetailsButton, onViewDetails, t);

    return columns;
  }, [dispatch, filterredList, history, state, t, classes]);

  const generatePayload = useCallback(
    () => ({
      ...(state.filters &&
        state.filters.examLevel &&
        state.filters.examLevel.length > 0 && { examLevel: state.filters.examLevel }),
      ...(state.filters &&
        state.filters.school &&
        state.filters.school.length > 0 && { schoolDesc: state.filters.school }),
      ...(state.filters &&
        state.filters.paymentMode &&
        state.filters.paymentMode.length > 0 && { paymentMode: state.filters.paymentMode }),
      ...(state.search && { search: state.search }),
    }),
    [state.filters, state.search]
  );

  /** init Data Table Option
   * */
  const initOptions = useMemo<MUIDataTableOptions>(() => {
    /** handle export excel action
     * */
    const onExportExcel = () => {
      onDownload({
        data: Object.keys(state.rowsSelected).map((i) => state.rowsSelected[i].indexNumber),
        payload: generatePayload(),
      });
    };

    /** add export excel button
     * */
    const addExportExcelAction = () => (
      <DownloadFromS3Action
        color="primary"
        actionType="link"
        requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
        label="Export Excel"
        onClick={onExportExcel}
        onDownload={(status) => onDownloadTemplate(status)}
        disable={state.data.length === 0}
      />
    );

    /** add custom sort
     * */
    const addCustomSort = (changedColumn: string, direction: 'desc' | 'asc') => {
      dispatch({
        type: 'UPDATE_SORT',
        payload: {
          sortColumn: changedColumn,
          order: direction,
        },
      });
    };

    /** to handle filter confirm
     * */
    const onFilterSubmit = async (_filterList: string[][]) => {
      setFilterredList({
        examLevel: examSelectedValues.map((e) => e.codeValueDescription),
        paymentMode: paymentSelectedValues.map((p) => p.description),
        school: schoolSelectedValues.map((s) => s.codeId),
      });
    };

    return {
      ...tOptions,
      selectableRows: 'multiple',
      filter: true,
      sort: true,
      pagination: true,
      onFilterConfirm: onFilterSubmit,
      onColumnSortChange: addCustomSort,
      customToolbar: addExportExcelAction,
      customToolbarSelect: addExportExcelAction,
      selectToolbarPlacement: 'none',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    examSelectedValues,
    fileUUID,
    paymentSelectedValues,
    schoolSelectedValues,
    state.data.length,
    tOptions,
  ]);

  const callMutation = useCallback(() => {
    mutate({
      size: state.size,
      page: state.page,
      order: state.order,
      payload: generatePayload(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutate, state.filters, state.order, state.page, state.search, state.size, generatePayload]);

  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    setTriggerMutate((prev) => prev + 1);
  }, [setTriggerMutate, state.filters, state.order, state.page, state.search, state.size]);

  return (
    <>
      <TitleHeader title={t('resit->>OpAdminResit.Registration Detail')} />
      <ContentTemplate type="full">
        <SectionHeader title={t('resit->>OpAdminResit.Resit Registration(s)')} />
        <Box className={classes.tableSearchContainer}>
          <SearchBar
            name="searchInput"
            ariaLabel="searchInput"
            defaultValue={state.search}
            placeholder="Search by Name/NRIC/FIN/Passport No."
            onSearch={(str) => {
              dispatch({
                type: 'UPDATE_SEARCH',
                payload: str,
              });
            }}
          />
        </Box>

        <MUIDataTable
          // @ts-ignore
          isLoading={listingStatus === 'loading'}
          // @ts-ignore
          state={state}
          dispatch={dispatch}
          components={{ TableToolbarSelect: () => selectionToolbar(undefined) }}
          // @ts-ignore
          data={state.data}
          columns={initColumns}
          options={initOptions}
        />
        <DownloadProgressModal
          fileDownloadStatus={downloadStatus}
          modalState={dialogState}
          onDialogClose={onDialogClose}
        />
      </ContentTemplate>
    </>
  );
};
export default OopAdminResitRegisterDetail;
